import { COLORS, FONTS } from '../styles/tokens';
import type { ColorTheme } from '../styles/tokens';
import { APP_ROLE_PREFIX } from '../constants/auth-methods';
import type { KeycloakUser } from '../api/keycloak-admin';

interface UserRowProps {
  user: KeycloakUser;
  isDark: boolean;
  colors: ColorTheme;
  roles: string[];
  isProcessing: boolean;
  onManageApps: () => void;
  onEdit: () => void;
}

/** Single row in the user management table: User (with Edit) / Apps. */
export function UserRow({
  user, isDark, colors, roles, isProcessing, onManageApps, onEdit,
}: UserRowProps): React.JSX.Element {
  const btnTextColor = isDark ? '#D0D0D0' : '#333333';
  const mutedColor = isDark ? '#7A7A7A' : '#888888';
  const isAdmin = roles.includes('admin');

  /** Assigned app slugs, alphabetized so each app holds a stable column position. */
  const appSlugs = roles
    .filter(r => r.startsWith(APP_ROLE_PREFIX))
    .map(r => r.replace(APP_ROLE_PREFIX, ''))
    .sort((a, b) => a.localeCompare(b));

  const badgeBase: React.CSSProperties = {
    padding: '2px 8px', borderRadius: '5px', fontFamily: FONTS.body, fontSize: '11px', whiteSpace: 'nowrap',
  };

  return (
    <div className="vx-stack-row" style={{ display: 'grid', gridTemplateColumns: '215px 1fr', gap: '16px', padding: '14px 20px', borderBottom: `1px solid ${colors.border}`, alignItems: 'flex-start', opacity: isProcessing ? 0.6 : 1 }}>
      {/* User cell */}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontFamily: FONTS.body, fontSize: '13px', fontWeight: 500, color: colors.text }}>
            {user.firstName} {user.lastName}
          </span>
          {isAdmin && (
            <span style={{ ...badgeBase, color: COLORS.accent.gold, backgroundColor: isDark ? 'rgba(244, 196, 63, 0.12)' : 'rgba(244, 196, 63, 0.08)', border: '1px solid rgba(244, 196, 63, 0.3)' }}>
              Vertex admin
            </span>
          )}
          {!user.enabled && (
            <span style={{ ...badgeBase, color: '#EF4444', backgroundColor: isDark ? 'rgba(239, 68, 68, 0.12)' : 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
              Disabled
            </span>
          )}
        </div>
        <div className="vx-user-email" style={{ fontFamily: FONTS.body, fontSize: '12px', color: colors.textSecondary, marginTop: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {user.email}
        </div>
        <div style={{ fontFamily: FONTS.mono, fontSize: '12px', color: mutedColor, marginTop: '2px' }}>
          {user.username}
        </div>
        <button
          onClick={onEdit}
          disabled={isProcessing}
          style={{
            marginTop: '8px', height: '26px', padding: '0 10px', borderRadius: '6px', border: `1px solid ${colors.border}`,
            backgroundColor: 'transparent', color: btnTextColor, fontFamily: FONTS.body, fontSize: '11px',
            cursor: isProcessing ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
          }}
        >
          Edit user
        </button>
      </div>

      {/* Apps cell — read-only chips + Manage entry point */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', minWidth: 0 }}>
        {appSlugs.map(slug => (
          <span key={slug} style={{
            padding: '3px 9px', borderRadius: '6px', fontFamily: FONTS.mono, fontSize: '12px',
            color: colors.text, backgroundColor: isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt,
            border: `1px solid ${colors.border}`, whiteSpace: 'nowrap',
          }}>{slug}</span>
        ))}
        <button
          onClick={onManageApps}
          disabled={isProcessing}
          style={{
            height: '26px', padding: '0 10px', borderRadius: '6px', border: `1px solid ${colors.border}`,
            backgroundColor: 'transparent', color: btnTextColor, fontFamily: FONTS.body, fontSize: '11px',
            cursor: isProcessing ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
          }}
        >
          Manage
        </button>
      </div>
    </div>
  );
}
